import { useCallback, useEffect, useRef } from "react";
import { apiRequest } from "../lib/api";
import { getAnonymousVisitorId } from "../lib/anonymousVisitor";

const PERCENT_MILESTONES = [
  [0.25, "watched_25"],
  [0.5, "watched_50"],
  [0.9, "watched_90"],
];

function storedJson(key, fallback) {
  try { return JSON.parse(window.localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; }
}

export function usePlaybackEngagement({ videoId, videoTitle, playing, currentTime, duration, playbackMode }) {
  const stateRef = useRef({ videoId: null, sent: new Set(), watchedSeconds: 0 });
  const latestRef = useRef({ videoTitle, currentTime, duration, playbackMode });
  latestRef.current = { videoTitle, currentTime, duration, playbackMode };

  useEffect(() => {
    stateRef.current = { videoId, sent: new Set(), watchedSeconds: 0 };
  }, [videoId]);

  const send = useCallback((engagementEvent) => {
    const state = stateRef.current;
    if (!videoId || state.videoId !== videoId || state.sent.has(engagementEvent)) return;
    state.sent.add(engagementEvent);
    const latest = latestRef.current;
    apiRequest("/api/theofast/events/engagement", {
      method: "POST",
      body: JSON.stringify({
        videoId,
        videoTitle: latest.videoTitle,
        engagementEvent,
        playbackMode: latest.playbackMode,
        currentTime: latest.currentTime,
        duration: latest.duration,
        watchedSeconds: state.watchedSeconds,
        anonymousId: getAnonymousVisitorId(),
      }),
    }).catch(() => {});
  }, [videoId]);

  useEffect(() => {
    if (!playing || !videoId) return undefined;
    send("player_start");

    const today = new Date().toISOString().slice(0, 10);
    try {
      const watchedVideos = storedJson("theofast-engaged-videos:v1", []);
      if (Array.isArray(watchedVideos) && watchedVideos.length && !watchedVideos.includes(videoId)) send("second_video");
      const nextVideos = Array.isArray(watchedVideos) ? [...new Set([...watchedVideos, videoId])].slice(-100) : [videoId];
      window.localStorage.setItem("theofast-engaged-videos:v1", JSON.stringify(nextVideos));
      const previousDay = window.localStorage.getItem("theofast-last-play-day");
      if (previousDay && previousDay < today) send("return_day");
      window.localStorage.setItem("theofast-last-play-day", today);
    } catch { /* Engagement milestones still work when storage is unavailable. */ }

    let previousTick = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const elapsed = Math.min(2, Math.max(0, (now - previousTick) / 1000));
      previousTick = now;
      if (document.visibilityState === "visible") stateRef.current.watchedSeconds += elapsed;
      if (stateRef.current.watchedSeconds >= 300) send("watched_5m");
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, send, videoId]);

  useEffect(() => {
    if (!playing || !duration || duration <= 0) return;
    const progress = currentTime / duration;
    PERCENT_MILESTONES.forEach(([threshold, event]) => {
      if (progress >= threshold) send(event);
    });
  }, [currentTime, duration, playing, send]);
}
